export class PerformanceProfiler {
    private static frameCount = 0;
    private static lastTime = performance.now();

    // Frame timing metrics
    private static currentFPS = 0;
    private static frameTimeSum = 0;
    private static worstFrameTime = 0;
    private static droppedFrames = 0;
    private static lastReportTime = performance.now();

    // Scene identity tracking
    private static previousSceneName = "NONE";
    private static currentSceneName = "INIT";

    // Lifecycle counters
    private static counters = {
        sceneCreated: 0,
        sceneDestroyed: 0,

        spineCreated: 0,
        spineDestroyed: 0,

        tweenCreated: 0,
        tweenDestroyed: 0,

        listenersAdded: 0,
        listenersRemoved: 0,

        bundleLoaded: 0,
        bundleUnloaded: 0,
    };

    /**
     * Must be called on EVERY unthrottled Pixi ticker frame.
     */
    public static update(): void {
        const now = performance.now();
        const delta = now - this.lastTime;
        this.lastTime = now;

        this.frameCount++;
        this.frameTimeSum += delta;

        if (delta > this.worstFrameTime) {
            this.worstFrameTime = delta;
        }

        // Count frames taking longer than ~16.67ms (60 FPS threshold)
        if (delta > 33.33) {
            this.droppedFrames++;
        }

        // Generate report every 1000ms
        if (now - this.lastReportTime >= 1000) {
            this.currentFPS = this.frameCount;
            this.logReport();

            // Reset per-second accumulators
            this.frameCount = 0;
            this.frameTimeSum = 0;
            this.worstFrameTime = 0;
            this.droppedFrames = 0;
            this.lastReportTime = now;
        }
    }

    /**
     * Scene identity tracking
     */
    public static trackSceneTransition(targetSceneName: string): void {
        this.previousSceneName = this.currentSceneName;
        this.currentSceneName = targetSceneName;

        console.log(`\n==================================================`);
        console.log(`[SCENE TRANSITION] ${this.previousSceneName} ➔ ${this.currentSceneName}`);
        console.log(`==================================================\n`);
    }

    /**
     * Counter increments / decrements
     */
    public static increment(key: keyof typeof PerformanceProfiler.counters): void {
        if (this.counters[key] !== undefined) {
            this.counters[key]++;
        }
    }

    public static decrement(key: keyof typeof PerformanceProfiler.counters): void {
        if (this.counters[key] !== undefined) {
            this.counters[key]--;
        }
    }

    /**
     * Live object getters
     */
    public static getLiveCount(
        createdKey: keyof typeof PerformanceProfiler.counters,
        destroyedKey: keyof typeof PerformanceProfiler.counters
    ): number {
        return this.counters[createdKey] - this.counters[destroyedKey];
    }

    private static logReport(): void {
        const memory = this.getMemory();
        const avgFrameTime = this.frameCount > 0 ? (this.frameTimeSum / this.frameCount) : 0;

        console.table({
            Scene: `${this.previousSceneName} ➔ ${this.currentSceneName}`,
            FPS: this.currentFPS,
            AvgFrame: `${avgFrameTime.toFixed(2)} ms`,
            WorstFrame: `${this.worstFrameTime.toFixed(2)} ms`,
            DroppedFrames: this.droppedFrames,
            JSHeapUsed: memory ? `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB` : "N/A",
            JSHeapTotal: memory ? `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB` : "N/A",
        });

        console.log(
            `[LIFECYCLE LIVE SUMMARY]\n` +
            `• Scenes    : Live ${this.getLiveCount("sceneCreated", "sceneDestroyed")} (Created: ${this.counters.sceneCreated} | Destroyed: ${this.counters.sceneDestroyed})\n` +
            `• Spine     : Live ${this.getLiveCount("spineCreated", "spineDestroyed")} (Created: ${this.counters.spineCreated} | Destroyed: ${this.counters.spineDestroyed})\n` +
            `• Tweens    : Live ${this.getLiveCount("tweenCreated", "tweenDestroyed")} (Created: ${this.counters.tweenCreated} | Destroyed: ${this.counters.tweenDestroyed})\n` +
            `• Listeners : Live ${this.getLiveCount("listenersAdded", "listenersRemoved")} (Added: ${this.counters.listenersAdded} | Removed: ${this.counters.listenersRemoved})\n` +
            `• Bundles   : Active ${this.getLiveCount("bundleLoaded", "bundleUnloaded")} (Loaded: ${this.counters.bundleLoaded} | Unloaded: ${this.counters.bundleUnloaded})`
        );
    }

    private static getMemory(): {
        usedJSHeapSize: number;
        totalJSHeapSize: number;
        jsHeapSizeLimit: number;
    } | null {
        const performanceWithMemory = performance as Performance & {
            memory?: {
                usedJSHeapSize: number;
                totalJSHeapSize: number;
                jsHeapSizeLimit: number;
            };
        };
        return performanceWithMemory.memory ?? null;
    }
}